try {
    if (process.env.TOKENS_FEATURE_FLAG === 'true' || process.env.STORYBOOK_TOKENS_FEATURE_FLAG !== 'false') require('./index-new.css');
    else require('./index.css');
  } catch (e) {
    if (e.code !== 'MODULE_NOT_FOUND') {
      throw e; // Re-throw unexpected errors
    }
    console.warn('index.css / index-new.css not found, proceeding without it.');
  }


import { jsx, jsxs } from 'react/jsx-runtime';
import classNames from 'classnames';
import React, { forwardRef } from 'react';
import { Image } from '@design-system/image';
import { Link } from '@design-system/link';
import { omitBy, cloneDeep, isNil, camelCase, isPlainObject, kebabCase, isEmpty } from 'lodash';

const FORBIDDEN_ATTR_CHARS = ['"', "'", '>', '<', '/', '='];

const prepareAttrs = (type, jsonValueAttrs, options) => {
    var _a, _b, _c, _d, _e;
    let orgType;
    const extraProps = {};
    if (!jsonValueAttrs)
        return { orgType, extraProps, attrs: { style: {} } };
    const allAttrs = omitBy(cloneDeep(jsonValueAttrs), isNil);
    const attrsJson = Object.assign({}, allAttrs.redactorAttributes);
    const style = {};
    if (type === 'reference' && allAttrs.default) {
        orgType = 'img';
        const inline = {};
        if (attrsJson.assetLink) {
            attrsJson.src = attrsJson.assetLink;
            delete attrsJson.assetLink;
            delete allAttrs.assetLink;
        }
        if (attrsJson.inline) {
            inline.display = 'flow-root';
            inline.margin = 0;
            delete attrsJson.width;
            delete attrsJson.style;
        }
        if (attrsJson.position) {
            extraProps.position = {
                style: attrsJson.position === 'center'
                    ? {
                        margin: 'auto',
                        textAlign: 'center',
                        width: `${allAttrs.width || 100}%`,
                    }
                    : Object.assign(Object.assign({ float: attrsJson.position }, inline), { width: `${allAttrs.width || 100}%`, maxWidth: `${allAttrs.maxWidth || 100}%` }),
            };
            extraProps.alignment = attrsJson.position;
            delete attrsJson.position;
            delete attrsJson.width;
            delete attrsJson.style;
            delete attrsJson.height;
            delete attrsJson.maxWidth;
            delete allAttrs.maxWidth;
            delete allAttrs.width;
            (_a = allAttrs.redactorAttributes) === null || _a === void 0 ? true : delete _a.width;
            (_b = allAttrs.redactorAttributes) === null || _b === void 0 ? true : delete _b.style;
            (_c = allAttrs.redactorAttributes) === null || _c === void 0 ? true : delete _c.maxWidth;
        }
        if (attrsJson.assetCaption) {
            extraProps.caption = attrsJson.assetCaption;
            delete attrsJson.assetCaption;
            delete allAttrs.assetCaption;
        }
        if (attrsJson.link) {
            extraProps.anchorLink = {
                href: attrsJson.link,
                target: attrsJson.target ? '_blank' : undefined,
            };
            delete attrsJson.link;
            delete allAttrs.link;
        }
        delete allAttrs.default;
        delete attrsJson.default;
        delete attrsJson.target;
        delete allAttrs.assetLink;
        delete allAttrs.assetType;
        delete allAttrs.displayType;
    }
    if (type === 'a') {
        attrsJson.href = allAttrs.url;
    }
    if (allAttrs.orgType) {
        orgType = allAttrs.orgType;
        delete allAttrs.orgType;
    }
    if (allAttrs.className) {
        attrsJson.className = allAttrs.className;
        delete allAttrs.className;
    }
    if (allAttrs.id) {
        attrsJson.id = allAttrs.id;
        delete allAttrs.id;
    }
    if (attrsJson.width || allAttrs.width) {
        const width = (attrsJson.width || allAttrs.width).toString();
        const height = ((_d = (attrsJson.height || allAttrs.height)) === null || _d === void 0 ? void 0 : _d.toString()) || 'auto';
        style.width =
            width.endsWith('%') || width.endsWith('px') ? width : `${width}px`;
        style.height =
            height.endsWith('%') || height.endsWith('px') || height === 'auto'
                ? height
                : `${height}px`;
    }
    if (allAttrs.style && type !== 'img') {
        Object.entries(allAttrs.style).forEach(([key, value]) => {
            style[camelCase(key)] = value;
        });
        delete allAttrs.style;
    }
    delete allAttrs.rows;
    delete allAttrs.cols;
    delete allAttrs.colWidths;
    delete allAttrs.disabledCols;
    delete allAttrs.colSpan;
    delete allAttrs.rowSpan;
    const allAttrsJson = Object.assign(Object.assign(Object.assign({}, attrsJson), allAttrs), { style });
    if (type === 'reference') {
        if (allAttrsJson.type === 'entry') {
            allAttrsJson['data-sys-entry-uid'] = allAttrs.entryUid;
            delete allAttrsJson.entryUid;
            allAttrsJson['data-sys-entry-locale'] = allAttrs.locale;
            delete allAttrsJson.locale;
            allAttrsJson['data-sys-content-type-uid'] = allAttrs.contentTypeUid;
            delete allAttrsJson.contentTypeUid;
            allAttrsJson['data-sys-style-type'] = allAttrs.displayType;
            delete allAttrsJson.displayType;
        }
        else if (allAttrsJson.type === 'asset') {
            allAttrsJson.src = allAttrs.assetLink;
            if (attrsJson.alt)
                allAttrsJson.alt = attrsJson.alt;
            if (attrsJson.caption)
                extraProps.caption = attrsJson.caption;
            if (attrsJson.anchorLink) {
                extraProps.anchorLink = {
                    href: attrsJson.anchorLink,
                    target: attrsJson.target ? '_blank' : undefined,
                };
                delete allAttrsJson.anchorLink;
                delete allAttrsJson.link;
            }
            if (attrsJson.position)
                extraProps.position = attrsJson.position;
            if (allAttrsJson.inline) {
                extraProps.inline = allAttrsJson.inline;
                delete allAttrsJson.inline;
            }
            allAttrsJson['data-sys-asset-filelink'] = allAttrs.assetLink;
            delete allAttrsJson.assetLink;
            allAttrsJson['data-sys-asset-uid'] = allAttrs.assetUid;
            delete allAttrsJson.assetUid;
            allAttrsJson['data-sys-asset-filename'] = allAttrs.assetName;
            delete allAttrsJson.assetName;
            allAttrsJson['data-sys-asset-contenttype'] = allAttrs.assetType;
            delete allAttrsJson.assetType;
            allAttrsJson['data-sys-content-type-uid'] = allAttrs.contentTypeUid;
            delete allAttrsJson.contentTypeUid;
            if (allAttrs.assetCaption) {
                allAttrsJson['data-sys-asset-caption'] = allAttrs.assetCaption;
                delete allAttrsJson.assetCaption;
            }
            if (allAttrs.assetAlt) {
                allAttrsJson['data-sys-asset-alt'] = allAttrs.assetAlt;
                delete allAttrsJson.assetAlt;
            }
            if (allAttrs.link) {
                allAttrsJson['data-sys-asset-link'] = allAttrs.link;
                delete allAttrsJson.link;
            }
            if (allAttrs.position) {
                allAttrsJson['data-sys-asset-position'] = allAttrs.position;
                delete allAttrsJson.position;
            }
            if (allAttrs.target) {
                allAttrsJson['data-sys-asset-isnewtab'] = allAttrs.target === '_blank';
                delete allAttrsJson.target;
            }
            if (!allAttrsJson['data-sys-style-type']) {
                allAttrsJson['data-sys-style-type'] =
                    String(allAttrs.assetType).indexOf('image') > -1
                        ? 'display'
                        : 'download';
            }
            if (allAttrsJson.displayType === 'display') {
                const styleObj = Object.assign(Object.assign({}, jsonValueAttrs.style), allAttrsJson.style);
                delete styleObj.float;
                allAttrsJson.style = Object.fromEntries(Object.entries(styleObj)
                    .filter(([_, val]) => val)
                    .map(([key, val]) => [camelCase(key), val]));
            }
            delete allAttrsJson.displayType;
        }
    }
    if (type === 'style') {
        delete allAttrsJson.styleText;
    }
    if (type === 'img') {
        allAttrsJson.src = allAttrs.url;
        if (allAttrs.caption)
            extraProps.caption = allAttrs.caption;
        if (allAttrs.position)
            extraProps.position = allAttrs.position;
        if (allAttrs.anchorLink)
            extraProps.anchorLink = {
                href: allAttrs.anchorLink,
                target: allAttrs.target,
            };
    }
    if (type === 'social-embeds' || type === 'embed') {
        allAttrsJson.src = encodeURI(String(allAttrs.src));
    }
    if (!((_e = options === null || options === void 0 ? void 0 : options.customElementTypes) === null || _e === void 0 ? void 0 : _e[type])) {
        delete allAttrsJson.url;
    }
    if (allAttrsJson.colspan) {
        allAttrsJson.colSpan = allAttrsJson.colspan;
        delete allAttrsJson.colspan;
    }
    if (allAttrsJson.rowspan) {
        allAttrsJson.rowSpan = allAttrsJson.rowspan;
        delete allAttrsJson.rowspan;
    }
    delete allAttrsJson.redactorAttributes;
    Object.entries(allAttrsJson).forEach(([key, value]) => {
        if (FORBIDDEN_ATTR_CHARS.some((char) => key.includes(char)) || !value) {
            delete allAttrsJson[key];
        }
    });
    return { orgType, extraProps, attrs: allAttrsJson };
};

const prepareText = (jsonValue, nodeKey, textWrappers) => {
    var _a, _b;
    let textNode = jsonValue.text;
    if (jsonValue.break) {
        textNode = (jsxs(React.Fragment, { children: [jsonValue.text, jsx("br", {})] }, nodeKey));
    }
    else if (jsonValue.text.includes('\n')) {
        const parts = jsonValue.text.split('\n');
        textNode = (jsx(React.Fragment, { children: parts.flatMap((part, index) => {
                if (index === 0)
                    return part;
                if (!part)
                    return jsx("br", {}, index);
                return [jsx("br", {}, index), part];
            }) }, nodeKey));
    }
    Object.entries(jsonValue).forEach(([key, value]) => {
        if (value && textWrappers[key]) {
            textNode = textWrappers[key](textNode, nodeKey, value);
        }
    });
    const style = {};
    if ((_a = jsonValue.attrs) === null || _a === void 0 ? void 0 : _a.style) {
        Object.entries(jsonValue.attrs.style).forEach(([key, value]) => {
            if (value !== null)
                style[camelCase(key)] = value;
        });
    }
    return jsonValue.id || jsonValue.classname || ((_b = jsonValue.attrs) === null || _b === void 0 ? void 0 : _b.style) ? (jsx("span", Object.assign({ id: jsonValue.id || undefined, className: jsonValue.classname || undefined, style: style }, { children: textNode }), nodeKey)) : (textNode);
};

const fieldsToRemove = [
    'assetLink',
    'position',
    'maxWidth',
    'maxHeight',
    'void',
    'inline',
];
const sanitizeAttrs = (attrs) => {
    const sanitizedAttrs = Object.assign({}, attrs);
    fieldsToRemove.forEach((field) => delete sanitizedAttrs[field]);
    return sanitizedAttrs;
};

const TEXT_WRAPPERS = {
    bold: (child, key) => jsx("strong", { children: child }, key),
    italic: (child, key) => jsx("em", { children: child }, key),
    underline: (child, key) => jsx("u", { children: child }, key),
    strikethrough: (child, key) => jsx("del", { children: child }, key),
    superscript: (child, key) => jsx("sup", { children: child }, key),
    subscript: (child, key) => jsx("sub", { children: child }, key),
    inlineCode: (child, key) => (jsx("span", Object.assign({ "data-type": "inlineCode" }, { children: child }), key)),
};
const ELEMENT_TYPES = {
    blockquote: ({ attrs, children }) => (jsx("blockquote", Object.assign({}, attrs, { children: children }))),
    h1: ({ attrs, children }) => jsx("h1", Object.assign({}, attrs, { children: children })),
    h2: ({ attrs, children }) => jsx("h2", Object.assign({}, attrs, { children: children })),
    h3: ({ attrs, children }) => jsx("h3", Object.assign({}, attrs, { children: children })),
    h4: ({ attrs, children }) => jsx("h4", Object.assign({}, attrs, { children: children })),
    h5: ({ attrs, children }) => jsx("h5", Object.assign({}, attrs, { children: children })),
    h6: ({ attrs, children }) => jsx("h6", Object.assign({}, attrs, { children: children })),
    img: ({ attrs, extraProps, linkComponent, imageComponent }) => {
        const figureStyles = extraProps;
        const LinkComponent = linkComponent || Link;
        const ImageComponent = imageComponent || Image;
        const key = attrs.key;
        const src = attrs.src;
        const alt = attrs.alt;
        const img = (jsx(ImageComponent, Object.assign({ src: src, alt: alt }, attrs, { uid: attrs['data-sys-asset-uid'] }), key));
        const imgWithAnchor = figureStyles.anchorLink ? (jsx(LinkComponent, Object.assign({ variant: "subtle" }, figureStyles.anchorLink, { style: { display: 'block' } }, { children: img }), key)) : (img);
        const caption = figureStyles.caption ? (jsx("figcaption", Object.assign({ style: {
                textAlign: figureStyles.alignment === 'center' ? 'center' : undefined,
            } }, { children: figureStyles.caption }))) : null;
        return figureStyles.position || figureStyles.caption ? (jsxs("figure", Object.assign({}, figureStyles.position, { children: [imgWithAnchor, caption] }), key)) : (imgWithAnchor);
    },
    embed: ({ attrs }) => (jsx("iframe", Object.assign({}, attrs))),
    p: ({ attrs, children }) => {
        if (children.some((child) => React.isValidElement(child) && child.type === 'figure'))
            return (jsx("div", Object.assign({}, attrs, { style: { overflow: 'hidden' } }, { children: jsx("span", { children: children }) })));
        return jsx("p", Object.assign({}, attrs, { children: children }));
    },
    ol: ({ attrs, children }) => jsx("ol", Object.assign({}, attrs, { children: children })),
    ul: ({ attrs, children }) => jsx("ul", Object.assign({}, attrs, { children: children })),
    code: ({ attrs, children }) => jsx("pre", Object.assign({}, attrs, { children: children })),
    li: ({ attrs, children }) => jsx("li", Object.assign({}, attrs, { children: children })),
    a: ({ attrs, children, linkComponent }) => {
        const LinkComponent = linkComponent || Link;
        const href = typeof attrs.href === 'string' ? attrs.href : '';
        return (jsx(LinkComponent, Object.assign({ variant: "subtle" }, attrs, { href: href }, { children: children })));
    },
    table: ({ attrs, children }) => jsx("table", Object.assign({}, attrs, { children: children })),
    tbody: ({ attrs, children }) => jsx("tbody", Object.assign({}, attrs, { children: children })),
    thead: ({ attrs, children }) => jsx("thead", Object.assign({}, attrs, { children: children })),
    tr: ({ attrs, children }) => jsx("tr", Object.assign({}, attrs, { children: children })),
    trgrp: ({ children }) => children,
    td: ({ attrs, children }) => jsx("td", Object.assign({}, attrs, { children: children })),
    th: ({ attrs, children }) => jsx("th", Object.assign({}, attrs, { children: children })),
    'check-list': ({ attrs, children }) => jsx("p", Object.assign({}, attrs, { children: children })),
    row: ({ attrs, children }) => jsx("div", Object.assign({}, attrs, { children: children })),
    column: ({ attrs, children }) => jsx("div", Object.assign({}, attrs, { children: children })),
    'grid-container': ({ attrs, children }) => jsx("div", Object.assign({}, attrs, { children: children })),
    'grid-child': ({ attrs, children }) => jsx("div", Object.assign({}, attrs, { children: children })),
    hr: ({ attrs }) => jsx("hr", Object.assign({}, attrs)),
    span: ({ attrs, children }) => jsx("span", Object.assign({}, attrs, { children: children })),
    div: ({ attrs, children }) => jsx("div", Object.assign({}, attrs, { children: children })),
    reference: ({ attrs, children, extraProps, linkComponent, imageComponent, }) => {
        const LinkComponent = linkComponent || Link;
        const ImageComponent = imageComponent || Image;
        const href = typeof attrs.href === 'string' ? attrs.href : '';
        switch (extraProps === null || extraProps === void 0 ? void 0 : extraProps.displayType) {
            case 'inline':
                return (jsx("span", Object.assign({}, attrs, { children: children }), attrs.key));
            case 'block':
                return (jsx("div", Object.assign({}, attrs, { children: children }), attrs.key));
            case 'link':
                return (jsx(LinkComponent, Object.assign({ variant: "subtle" }, attrs, { href: href }, { children: children }), attrs.key));
            case 'asset':
                return (jsx("figure", Object.assign({}, attrs, { children: children }), attrs.key));
            case 'display': {
                const { anchorLink, caption, position, inline } = extraProps;
                const key = attrs.key;
                const src = attrs.src;
                const alt = attrs.alt;
                const figureStyles = {};
                let figureClasses = 'cds-json-rte__figure';
                if (position && position === 'left') {
                    figureClasses += ' cds-json-rte__figure--float-left';
                }
                else if (position && position === 'right') {
                    figureClasses += ' cds-json-rte__figure--float-right';
                }
                const img = (jsx(ImageComponent, Object.assign({ src: src, alt: alt }, attrs, { uid: attrs['data-sys-asset-uid'] }), key));
                const imgWithAnchor = anchorLink ? (jsx(LinkComponent, Object.assign({ variant: "subtle" }, anchorLink, { style: { display: 'inline-block', maxWidth: '100%' } }, { children: img }), key)) : (img);
                const imgWithCaption = caption ? (jsxs("div", Object.assign({ style: { display: 'inline-block' } }, { children: [imgWithAnchor, jsx("figcaption", Object.assign({ style: { textAlign: 'center' } }, { children: caption }))] }))) : (imgWithAnchor);
                if (inline && (!position || position === 'none'))
                    figureStyles.display = 'inline-block';
                if (position && position !== 'none') {
                    figureStyles[inline ? 'float' : 'textAlign'] = position;
                }
                return (jsx("figure", Object.assign({ style: figureStyles, className: figureClasses }, { children: imgWithCaption }), key));
            }
            default:
                return (jsx("span", Object.assign({}, attrs, { children: children }), attrs.key));
        }
    },
    inlineCode: () => '',
    fragment: ({ children }) => children,
    style: ({ attrs, children }) => jsx("style", Object.assign({}, attrs, { children: children })),
    script: ({ attrs, children }) => jsx("script", Object.assign({}, attrs, { children: children })),
    'social-embeds': ({ attrs }) => (jsx("iframe", Object.assign({}, attrs, { "data-type": "social-embeds" }))),
};
const isBlock = (jsonValue) => !!jsonValue.type;
const isLeaf = (jsonValue) => !!jsonValue.text;
const jsonToReactInner = ({ data, options, linkComponent, imageComponent, nodeKey, textWrappers, elementTypes, }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    if (isLeaf(data) && (data.text || data.text === ''))
        return prepareText(data, nodeKey, textWrappers);
    if (!isBlock(data))
        return null;
    let children = [];
    if (data.children) {
        children = data.children.map((child, index) => jsonToReactInner({
            data: child,
            options,
            linkComponent,
            imageComponent,
            nodeKey: index,
            textWrappers,
            elementTypes,
        }));
        if (data.type === 'blockquote') {
            children = children.map((child) => (child === '\n' ? jsx("br", {}) : child));
        }
    }
    if ((options === null || options === void 0 ? void 0 : options.allowNonStandardTypes) &&
        !Object.keys(elementTypes).includes(data.type) &&
        data.type !== 'doc') {
        const attrs = {};
        Object.entries(data.attrs || {}).forEach(([key, val]) => {
            if (isPlainObject(val)) {
                attrs[kebabCase(key)] = JSON.stringify(val);
            }
            else {
                attrs[kebabCase(key)] = val || true;
            }
        });
        console.warn(`${data.type} is not a valid element type.`);
        return React.createElement(data.type, attrs, children);
    }
    if (elementTypes[data.type]) {
        const { attrs, orgType, extraProps } = prepareAttrs(data.type, data.attrs, options);
        if (data.type === 'table' && ((_a = data.attrs) === null || _a === void 0 ? void 0 : _a.colWidths) && ((_b = data.attrs) === null || _b === void 0 ? void 0 : _b.cols)) {
            const { colWidths, cols } = data.attrs;
            const totalWidth = colWidths.reduce((a, b) => a + b, 0);
            const setCol = [...new Set(colWidths)];
            if (setCol.length !== 1 || cols * setCol[0] !== totalWidth) {
                const colgroup = (jsx("colgroup", Object.assign({ "data-width": totalWidth }, { children: colWidths.map((colWidth, index) => {
                        const width = (colWidth / totalWidth) * 100;
                        return jsx("col", { style: { width: `${width}%` } }, index);
                    }) }), "colgroup"));
                children = [colgroup, ...children];
            }
        }
        if (data.type === 'check-list') {
            attrs['data-checked'] = data.checked;
            attrs['data-type'] = 'checked';
        }
        if (data.type === 'row') {
            attrs['data-type'] = 'row';
            attrs.style.maxWidth = '100%';
            attrs.style.display = 'flex';
        }
        if (data.type === 'column') {
            const { width } = data.meta || { width: 1 };
            attrs['data-type'] = 'column';
            attrs.width = width;
            attrs.style = Object.assign(Object.assign({}, attrs.style), { flexGrow: 0, flexShrink: 0, position: 'relative', width: `${width * 100}%`, margin: '0 0.25rem' });
        }
        if (data.type === 'grid-container') {
            const gutter = (_c = data.attrs) === null || _c === void 0 ? void 0 : _c.gutter;
            attrs['data-type'] = 'grid-container';
            attrs.gutter = gutter;
            attrs.style = Object.assign(Object.assign({}, attrs.style), { display: 'flex', width: '100%', gap: `${gutter}px` });
        }
        if (data.type === 'grid-child') {
            const gridRatio = (_d = data.attrs) === null || _d === void 0 ? void 0 : _d.gridRatio;
            attrs['data-type'] = 'grid-child';
            attrs['grid-ratio'] = gridRatio;
            attrs.style.flex = gridRatio;
        }
        if (data.type === 'reference') {
            extraProps.displayType = (_e = data.attrs) === null || _e === void 0 ? void 0 : _e.displayType;
        }
        if (data.type === 'span' &&
            ((_f = data.children) === null || _f === void 0 ? void 0 : _f.length) === 1 &&
            isBlock(data.children[0]) &&
            data.children[0].type === 'span' &&
            isEmpty(data.attrs)) {
            return children;
        }
        if (data.uid) {
            attrs['data-json-rte-component-uid'] = data.uid;
            attrs.key = data.uid;
        }
        if (['td', 'th'].includes(data.type) && ((_g = data.attrs) === null || _g === void 0 ? void 0 : _g.void))
            return null;
        return elementTypes[orgType || data.type]({
            attrs: sanitizeAttrs(attrs),
            children,
            extraProps,
            linkComponent,
            imageComponent,
        });
    }
    return children;
};
const jsonToReact = (props) => {
    const { options } = props;
    const textWrappers = Object.assign(Object.assign({}, TEXT_WRAPPERS), options === null || options === void 0 ? void 0 : options.customTextWrappers);
    const elementTypes = Object.assign(Object.assign({}, ELEMENT_TYPES), options === null || options === void 0 ? void 0 : options.customElementTypes);
    return jsonToReactInner(Object.assign(Object.assign({}, props), { nodeKey: 'root', textWrappers,
        elementTypes }));
};

const JsonRTE = forwardRef(({ className, data, options, linkComponent, imageComponent }, ref) => {
    const items = jsonToReact({ data, options, linkComponent, imageComponent });
    return (jsx("div", Object.assign({ ref: ref, className: classNames('cds-json-rte', className) }, { children: items })));
});

export { JsonRTE };
